import React, { useState, createContext, useCallback, useContext } from 'react';
import type { PersonFragment } from '@graphql/fragments/person.graphql.generated';
import type { SmartTagRuleInput } from '@graphql/generated-base';
import type { SelectedPeople, SelectedPeopleDispatch } from '@shared/context/people.types';
import { getSessionStorage } from '@shared/core';
import { addError } from '@shared/utils/logger';

export const PeopleContext = createContext<{
  selectedPeople: SelectedPeople;
  setSelectedPeople: SelectedPeopleDispatch;
  clearSelectedPeople: () => void;
  /**
   * The smart tag the host picked instead of guests (MSG-245): a rule contact_service resolves when the
   * message sends. Never set alongside a non-empty `selectedPeople` — the provider clears one when the
   * other is set, because the server rejects a draft carrying both.
   */
  smartRule: SmartTagRuleInput | null;
  setSmartRule: (rule: SmartTagRuleInput | null) => void;
}>({
  selectedPeople: [],
  setSelectedPeople() {},
  clearSelectedPeople() {},
  smartRule: null,
  setSmartRule() {},
});

const buildSessionKey = (storageKey: string) => `people:${storageKey}`;
const buildSmartRuleSessionKey = (storageKey: string) => `people:${storageKey}:smartRule`;

const readPersistedSmartRule = (storageKey: string | undefined): SmartTagRuleInput | null => {
  if (!storageKey) return null;
  try {
    const raw = getSessionStorage().getItem(buildSmartRuleSessionKey(storageKey));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && 'resolver' in parsed ? (parsed as SmartTagRuleInput) : null;
  } catch (error) {
    addError(error, { context: 'PeopleContext#readPersistedSmartRule' });
    return null;
  }
};

const persistSmartRule = (storageKey: string | undefined, rule: SmartTagRuleInput | null) => {
  if (!storageKey) return;
  try {
    if (rule) getSessionStorage().setItem(buildSmartRuleSessionKey(storageKey), JSON.stringify(rule));
    else getSessionStorage().removeItem(buildSmartRuleSessionKey(storageKey));
  } catch (error) {
    addError(error, { context: 'PeopleContext#persistSmartRule' });
  }
};

const readPersistedPeople = (storageKey: string | undefined): ReadonlyArray<Partial<PersonFragment>> => {
  if (!storageKey) return [];
  try {
    const raw = getSessionStorage().getItem(buildSessionKey(storageKey));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    addError(error, { context: 'PeopleContext#readPersistedPeople' });
    return [];
  }
};

/** Writes a selection where a `PeopleProvider` mounted on `storageKey` will read it. */
export const persistPeople = (storageKey: string | undefined, people: ReadonlyArray<Partial<PersonFragment>>) => {
  if (!storageKey) return;
  try {
    getSessionStorage().setItem(buildSessionKey(storageKey), JSON.stringify(people));
  } catch (error) {
    addError(error, { context: 'PeopleContext#persistPeople' });
  }
};

/** Drops the selection persisted under `storageKey`. A provider already mounted on it keeps its own state. */
export const clearPersistedPeople = (storageKey: string): void => {
  try {
    getSessionStorage().removeItem(buildSessionKey(storageKey));
    getSessionStorage().removeItem(buildSmartRuleSessionKey(storageKey));
  } catch {
    // best-effort cleanup
  }
};

type PeopleProviderProps = Readonly<{
  children: React.ReactNode;
  /** When provided, the selection is persisted to sessionStorage under this key so it survives page refreshes. */
  storageKey?: string;
}>;

type Audience = Readonly<{
  selectedPeople: ReadonlyArray<Partial<PersonFragment>>;
  smartRule: SmartTagRuleInput | null;
}>;

export const PeopleProvider: React.FC<PeopleProviderProps> = ({ children, storageKey }) => {
  // One state, so the "never both" rule is applied in the same update that sets either half.
  const [audience, setAudience] = useState<Audience>(() => ({
    selectedPeople: readPersistedPeople(storageKey),
    smartRule: readPersistedSmartRule(storageKey),
  }));

  const setSelectedPeople: SelectedPeopleDispatch = useCallback(
    valueOrUpdater => {
      setAudience(prev => {
        const next = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev.selectedPeople) : valueOrUpdater;
        persistPeople(storageKey, next);
        // Picking guests replaces a smart tag. An empty list doesn't: the picker's enrichment pass
        // rewrites the selection in place, and an empty rewrite is not a choice of audience.
        const smartRule = next.length > 0 ? null : prev.smartRule;
        // Same references in → same state out, so React bails out. The picker's enrichment effect
        // returns `prev` unchanged to stop its own loop, and a fresh wrapper object here would undo that.
        if (next === prev.selectedPeople && smartRule === prev.smartRule) return prev;
        if (smartRule !== prev.smartRule) persistSmartRule(storageKey, smartRule);
        return { selectedPeople: next, smartRule };
      });
    },
    [storageKey],
  );

  const setSmartRule = useCallback(
    (rule: SmartTagRuleInput | null) => {
      setAudience(prev => {
        if (!rule && !prev.smartRule) return prev;
        persistSmartRule(storageKey, rule);
        if (!rule) return { ...prev, smartRule: null };
        persistPeople(storageKey, []);
        return { selectedPeople: [], smartRule: rule };
      });
    },
    [storageKey],
  );

  const clearSelectedPeople = useCallback(() => {
    setAudience({ selectedPeople: [], smartRule: null });
    if (storageKey) clearPersistedPeople(storageKey);
  }, [storageKey]);

  return (
    <PeopleContext.Provider
      value={{
        selectedPeople: audience.selectedPeople,
        setSelectedPeople,
        clearSelectedPeople,
        smartRule: audience.smartRule,
        setSmartRule,
      }}
    >
      {children}
    </PeopleContext.Provider>
  );
};

export const usePeopleContext = () => {
  return useContext(PeopleContext);
};
